<template>
	<div class="app-container no-padding no-scroll" :class="{ content__admin: isEditor }">
		
		<ArticleAdminRibbon
			v-if="isEditor"
			:disabled="isHistorical"
			:article="article"
			:loading="loading"
			@pingsaveineditor="saveArticle"
			@togglepublish="togglePublish"
		/>

		<div class="layout columns">
		
			<div class="column localnav">

				<SideNavigation v-if="!error" :articleJson="article.json" :title="article.title" :articleId="article.id" :itemInView="itemInView" />

			</div>
		
			<div class="column article">

				<ArticleBreadCrumbs v-if="!isHistorical" :article="article" />
				<ArticleHistoryHeader v-if="isHistorical" :articleHistory="article" />
				<Editor v-if="!error" @itemscrolledinview="onItemInView" :isAdmin="isEditor && !isHistorical" :article="displayArticle" :editable="false" ref="editor" />
				<Spinner v-if="loadingUser" />
				<ErrorAlert class="mt-5" v-if="error" :error="error" />

			</div>
		
			<div class="column info">

				<SidePanel v-if="!error" :relatedArticles="article.articlesMinimal" :articleId="isHistorical ? article.articleId : article.id" :boxArticlesIds="boxArticlesIds" :isHistorical="isHistorical" ref="sidePanel" />

			</div>
		
		</div>

		<ErrorDialog ref="errDialog" />
	</div>
</template>

<script>
import SideNavigation from './SideNavigation';
import Editor from './Editor';
import Article from '../../../services/article';
import ArticleHistory from '../../../services/article-history';
import SidePanel from './SidePanel';
import Spinner from '../../shared/Spinner';
import ErrorAlert from '../../shared/ErrorAlert';
import ErrorDialog from '../../shared/ErrorDialog';
import ArticleBreadCrumbs from './ArticleBreadCrumbs';
import GridBuilder from '../../../util/classes/GridBuilder';
import LinkHighlight from '../../../util/classes/LinkHighlight';
import ArticleAdminRibbon from './ArticleAdminRibbon';
import ArticleHistoryHeader from './ArticleHistoryHeader';
import { mapGetters } from 'vuex';

export default {
	name: 'ArticleView',
	components: {
		SideNavigation,
		Editor,
		SidePanel,
		Spinner,
		ErrorAlert,
		ErrorDialog,
		ArticleBreadCrumbs,
		ArticleAdminRibbon,
		ArticleHistoryHeader,
	},
	props: ['scrollFromEdit'],
	data() {
		return {
			article: { html: '' },
			displayArticle: {},
			parentArticles: [],
			articleTypes: [],
			boxArticlesIds: [],
			loading: false,
			loadingUser: false,
			itemInView: '',
			error: null,
			isHistorical: false,
		};
	},
	mounted() {
		this.loadArticle();
	},
	methods: {
		saveArticle: function () {
			if (!this.article.title || this.article.title.length == 0) {
				this.$refs.errDialog.show({
					message: 'Article must have a title!',
				});
				return;
			}
			this.article.html = LinkHighlight.clearLinks(this.article.html);
			this.executeArticleSaveReq(this.article);
		},
		executeArticleSaveReq: function (payload) {
			this.loading = true;
			Article.instance()
				.editArticle(payload)
				.then((res) => {
					this.loading = false;
					this.article = res.data;
					this.$store.dispatch('fetchIndexArticles');
					if (this.isEditor) this.$refs.sidePanel.getArticleHistory();
				})
				.catch((error) => {
					this.$refs.errDialog.show(error);
					this.loading = false;
				});
		},
		loadArticle: function () {
			let articleId = this.$route.query.id,
				articleCode = this.$route.query.code,
				historyId = this.$route.query.historyId;

			this.isHistorical = false;

			if (!articleId && !articleCode && !historyId) {
				return;
			}

			if (this.article && articleId && this.article.id == articleId) {
				return;
			}

			if (articleId) {
				this.getArticle(articleId);
			}

			if (articleCode) {
				this.getArticleByCode(articleCode);
			}

			if (historyId) {
				this.getArticleHistory(historyId);
				this.isHistorical = true;
			}
		},
		getArticle: function (id) {
			this.loadingUser = true;
			Article.instance()
				.getArticleById(id)
				.then((res) => {
					this.removeError();
					this.article = res.data;
					this.highlightLinks();
					this.generateGrid();
				})
				.catch((error) => {
					this.addError(error);
					this.loadingUser = false;
				});
		},
		getArticleByCode: function (code) {
			this.loadingUser = true;
			Article.instance()
				.getArticleByCode(code)
				.then((res) => {
					this.removeError();
					this.article = res.data;
					this.highlightLinks();
					this.generateGrid();
				})
				.catch((error) => {
					this.addError(error);
					this.loadingUser = false;
				});
		},
		getArticleHistory: function (historyId) {
			this.loadingUser = true;
			ArticleHistory.instance()
				.getArticleHistory(historyId)
				.then((res) => {
					this.removeError();
					this.article = res.data;
					this.highlightLinks();
					this.generateGrid();
				})
				.catch((error) => {
					this.addError(error);
					this.loadingUser = false;
				});
		},
		highlightLinks: function () {
			let linkHighlighter = new LinkHighlight(
				this.article.html,
				this.article.articlesMinimal,
				this.article.id
			);
			this.article.html = linkHighlighter.getHighlightHtml();
		},
		generateGrid: async function () {
			let gridBuilder = new GridBuilder(this.article.html);
			let displayHtml = await gridBuilder.getDisplayHtmlAsync();
			this.boxArticlesIds = gridBuilder.getBoxedArticleIds();
			this.displayArticle = { ...this.article, html: displayHtml };
			this.loadingUser = false;
		},
		togglePublish: function () {
			this.article.published = !this.article.published;
			this.saveArticle();
		},
		onItemInView: function (id) {
			this.itemInView = id;
		},
		addError: function (error) {
			this.error = error;
		},
		removeError: function () {
			this.error = null;
		},
	},
	computed: {
		...mapGetters(['isEditor']),
	},
	watch: {
		'$route.query.id': 'loadArticle',
		'$route.query.code': 'loadArticle',
		'$route.query.historyId': 'loadArticle',
	},
};
</script>

<style scss>

.layout.columns {
    flex: 1 1 auto;
    display: flex;
    overflow: hidden;

    .column {
        flex: 0 0 20%;

        &.localnav {
            padding: 0;
            background: #f8f8f8;
            opacity: 0.8;
            transition: opacity 0.2s ease-in;
            color: #666;
			display: flex;
			flex-direction: column;

            @media (max-width: 960px) {
                display: none;
            }

            &:hover {
                opacity: 1;
            }
        }

        &.article {
            flex: 1 1 40%;
            position: relative;
            z-index: 5;
            box-shadow: 0 0 1em 0 rgb(0, 0, 0, 0.1);
            overflow: hidden;
            display: flex;
            flex-direction: column;

            .breadcrumbs {
                box-shadow: 0 0 1em 0 rgb(0, 0, 0, 0.1);
				position: relative;
				z-index: 100;
            }

			.content {
				flex: 1 1 auto;
				height: auto;
				padding: 2em 3em;
				overflow: auto;
				position: relative;
				z-index: 50;
				scrollbar-color: #DDD transparent;
				scrollbar-width: thin;
			}
        }

        &.info {
            background: #F8F8F8;
            padding: 0;
            opacity: 0.5;
            transition: opacity 0.2s ease-in;

            @media (max-width: 960px) {
                flex-basis: 30%;
            }

            @media (max-width: 740px) {
                position: absolute;
                bottom: 1em;
                right: 1em;
                width: 40%;
				height: auto;
                background: #fff;
                opacity: 1 !important;
                z-index: 1000;
                border-radius: 0.3em;
                box-shadow: 0 0 1em 0 rgb(0, 0, 0, 0.1);
                padding: 0;
            }


            &:hover {
                opacity: 1;
            }
        }
    }
}




.content__options-grp {
	margin-right: 2rem;
}
.content__options-label {
	color: var(--textTertiary);
	text-transform: uppercase;
	margin-right: 1rem;
}
.content__options-select {
	color: var(--textAdmin);
	background-color: #fff;
	border: none;
	border-radius: 2px;
	padding: 5px;
	font-weight: bold;
}
.content__options-select option {
	margin-bottom: 15px;
	margin-top: 15px;
}
.content__options-select:hover {
	cursor: pointer;
}
.content__options-select:focus {
	outline: none;
}
.content__options-save {
	color: var(--textAdmin);
	display: flex;
	align-items: center;
}
.content__options-save:hover {
	cursor: pointer;
}
.content__options-delim {
	width: 1px;
	height: 2rem;
	background-color: #9d8fe3;
	margin-left: 3rem;
	margin-right: 3rem;
}
.content__options-loading {
	position: absolute;
	right: 15px;
	top: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
.content__grid-e {
	position: relative;
}
.slider {
	display: flex;
	align-items: center;
	margin-right: 3rem;
}
.slider__text {
	color: var(--textAdmin);
	margin-right: 1rem;
	width: 9rem;
}
.slider__btn {
	display: flex;
	align-items: center;
	width: 40px;
	height: 14px;
	border-radius: 10px;
	background-color: #c6beed;
	transition: background 200ms ease;
}
.slider__btn-active {
	background-color: #7c64f5;
}
.slider__btn:hover {
	cursor: pointer;
}
.slider__dot {
	width: 20px;
	height: 20px;
	background-color: var(--textAdmin);
	border-radius: 50%;
	transition: transform 200ms ease, background 200ms ease;
	box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);
}
.slider__left {
	transform: translateX(0);
}
.slider__right {
	transform: translateX(100%);
}
.slider__active {
	background-image: url('../../../assets/icons/check-white.svg');
	background-repeat: no-repeat;
	background-position: center;
	background-size: 70%;
}
.slider__inactive {
	background-color: #efedfa;
	display: flex;
	align-items: center;
}

.slider__inactive::before {
	content: '!';
	color: #2a00ff;
	display: block;
	width: 100%;
	text-align: center;
}

.it-bold {
	font-style: italic;
	font-weight: bold;
}

</style>
